import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Activity, Lock, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../config/axios';
import LoadingSpinner from '../components/UI/LoadingSpinner';

const ResetPassword = () => {
    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [verifying, setVerifying] = useState(true);
    const [validToken, setValidToken] = useState(false);

    const { token } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        const verifyToken = async () => {
            try {
                const response = await api.get(`/api/auth/verify-reset-token/${token}`);
                setValidToken(response.data.valid);
            } catch (error) {
                setValidToken(false);
                toast.error('Invalid or expired reset token');
            } finally {
                setVerifying(false);
            }
        };

        if (token) {
            verifyToken();
        }
    }, [token]);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formData.password !== formData.confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        if (formData.password.length < 6) {
            toast.error('Password must be at least 6 characters long');
            return;
        }

        setLoading(true);

        try {
            await api.post('/api/auth/reset-password', {
                token,
                password: formData.password
            });

            toast.success('Password reset successful!');
            setTimeout(() => {
                navigate('/login');
            }, 1500);
        } catch (error) {
            const message = error.response?.data?.error || 'Failed to reset password';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    if (verifying) {
        return (
            <div className="h-screen flex items-center justify-center bg-gray-50 dark:bg-black transition-colors duration-200">
                <div className="text-center">
                    <LoadingSpinner size="lg" />
                    <p className="mt-4 text-gray-600 dark:text-gray-400">Verifying reset token...</p>
                </div>
            </div>
        );
    }

    if (!validToken) {
        return (
            <div className="h-screen flex items-center justify-center bg-gray-50 dark:bg-black transition-colors duration-200 px-4 sm:px-6 lg:px-8">
                <div className="max-w-md w-full space-y-8">
                    <div className="text-center">
                        <div className="flex justify-center">
                            <div className="p-3 bg-red-600 rounded-full">
                                <Activity className="h-8 w-8 text-white" />
                            </div>
                        </div>
                        <h2 className="mt-6 text-responsive-2xl font-bold text-gray-900 dark:text-gray-100">
                            Invalid Reset Link
                        </h2>
                        <p className="mt-2 text-responsive-base text-gray-600 dark:text-gray-400">
                            This password reset link is invalid or has expired.
                        </p>
                    </div>

                    <div className="mt-8 space-y-4">
                        <Link
                            to="/forgot-password"
                            className="w-full btn-primary py-3 flex items-center justify-center"
                        >
                            Request New Reset Link
                        </Link>
                        <Link
                            to="/login"
                            className="w-full btn-secondary py-3 flex items-center justify-center gap-2"
                        >
                            <ArrowLeft className="h-5 w-5" />
                            Back to Login
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="h-screen flex items-center justify-center bg-gray-50 dark:bg-primary-950 transition-colors duration-200 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div className="text-center">
                    <div className="flex justify-center">
                        <div className="p-3 bg-primary-600 rounded-full">
                            <Activity className="h-8 w-8 text-white" />
                        </div>
                    </div>
                    <h2 className="mt-6 text-responsive-2xl font-bold text-gray-900 dark:text-gray-100">
                        Reset Your Password
                    </h2>
                    <p className="mt-2 text-responsive-sm text-gray-600 dark:text-gray-400">
                        Enter your new password below
                    </p>
                </div>

                <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                    <div className="space-y-4">
                        <div>
                            <label htmlFor="password" className="sr-only">
                                New Password
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Lock className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    id="password"
                                    name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    autoComplete="new-password"
                                    required
                                    className="input pl-10 pr-10"
                                    placeholder="New password"
                                    value={formData.password}
                                    onChange={handleChange}
                                />
                                <button
                                    type="button"
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? (
                                        <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                                    ) : (
                                        <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                                    )}
                                </button>
                            </div>
                        </div>

                        <div>
                            <label htmlFor="confirmPassword" className="sr-only">
                                Confirm New Password
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Lock className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    autoComplete="new-password"
                                    required
                                    className="input pl-10 pr-10"
                                    placeholder="Confirm new password"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                />
                                <button
                                    type="button"
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                >
                                    {showConfirmPassword ? (
                                        <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                                    ) : (
                                        <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>

                    <div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full btn-primary py-3 text-responsive-base flex items-center justify-center"
                        >
                            {loading ? (
                                <LoadingSpinner size="sm" />
                            ) : (
                                'Reset Password'
                            )}
                        </button>
                    </div>

                    <div className="text-center">
                        <Link
                            to="/login"
                            className="text-responsive-sm text-primary-600 dark:text-primary-400 hover:text-primary-500 dark:hover:text-primary-300 flex items-center justify-center gap-2"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            Back to Login
                        </Link>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ResetPassword;
